import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PropositoAdjunto, subirAdjuntoDirecto, type Adjunto } from "../api";
import { formatearBytes } from "../formato";

type SubidorProps = {
  abierto: boolean;
  onCerrar: () => void;
  mascotaID: string;
  visitaID?: string | null;
  visitaItemID?: number | null;
  propositoInicial?: PropositoAdjunto;
  onCompletado?: (adjunto: Adjunto) => void;
};

const OPCIONES_PROPOSITO = [
  { valor: PropositoAdjunto.Carnet, nombre: "Carnet de vacunas", icono: "💉" },
  { valor: PropositoAdjunto.Examen, nombre: "Examen médico (sangre, orina, imagen)", icono: "🔬" },
  { valor: PropositoAdjunto.Receta, nombre: "Receta o indicación médica", icono: "💊" },
  { valor: PropositoAdjunto.FotoPerfil, nombre: "Foto de perfil", icono: "📷" },
  { valor: PropositoAdjunto.Otro, nombre: "Otro documento o foto", icono: "📁" },
];

export default function SubidorAdjuntoModal({
  abierto,
  onCerrar,
  mascotaID,
  visitaID,
  visitaItemID,
  propositoInicial = PropositoAdjunto.Carnet,
  onCompletado,
}: SubidorProps) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [proposito, setProposito] = useState<PropositoAdjunto>(propositoInicial);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  if (!abierto) return null;

  const seleccionarArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      setError("El archivo supera el límite de 15 MB.");
      return;
    }

    setArchivo(file);
    setError(null);

    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const ejecutarSubida = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!archivo) {
      setError("Selecciona un archivo para subir.");
      return;
    }

    try {
      setSubiendo(true);
      setError(null);

      const adjunto = await subirAdjuntoDirecto(archivo, {
        mascotaID,
        visitaID,
        visitaItemID,
        proposito,
      });

      qc.invalidateQueries({ queryKey: ["adjuntos", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascota", mascotaID] });
      if (visitaID) qc.invalidateQueries({ queryKey: ["visita", visitaID] });
      qc.invalidateQueries({ queryKey: ["visitas", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });

      onCompletado?.(adjunto);
      cerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al subir el archivo.");
    } finally {
      setSubiendo(false);
    }
  };

  const cerrar = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setArchivo(null);
    setPreviewUrl(null);
    setError(null);
    onCerrar();
  };

  return (
    <div className="modal-backdrop" onClick={cerrar}>
      <div className="modal-dialog panel" onClick={(e) => e.stopPropagation()}>
        <header className="modal-cabecera">
          <h2>Subir foto o documento</h2>
          <button type="button" className="boton-chico" onClick={cerrar} disabled={subiendo}>
            ✕
          </button>
        </header>

        <form onSubmit={ejecutarSubida} className="modal-form">
          {error && <p className="err">{error}</p>}

          <div className="campo">
            <label htmlFor="select-proposito">Tipo de documento</label>
            <select
              id="select-proposito"
              value={proposito}
              disabled={subiendo}
              onChange={(e) => setProposito(Number(e.target.value) as PropositoAdjunto)}
            >
              {OPCIONES_PROPOSITO.map((op) => (
                <option key={op.valor} value={op.valor}>
                  {op.icono} {op.nombre}
                </option>
              ))}
            </select>
          </div>

          <div
            className={`dropzone ${archivo ? "con-archivo" : ""}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              style={{ display: "none" }}
              onChange={seleccionarArchivo}
              disabled={subiendo}
            />

            {previewUrl ? (
              <div className="dropzone-preview">
                <img src={previewUrl} alt="Vista previa" className="dropzone-img" />
                <p className="dropzone-nombre">
                  <strong>{archivo?.name}</strong> ({formatearBytes(archivo?.size ?? 0)})
                </p>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  Clic para cambiar de archivo
                </span>
              </div>
            ) : archivo ? (
              <div className="dropzone-archivo">
                <span style={{ fontSize: "2.5rem" }}>📄</span>
                <p><strong>{archivo.name}</strong></p>
                <p className="muted">{formatearBytes(archivo.size)}</p>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  Clic para cambiar de archivo
                </span>
              </div>
            ) : (
              <div className="dropzone-vacio">
                <span style={{ fontSize: "2.5rem" }}>📤</span>
                <p><strong>Haz clic para seleccionar un archivo</strong></p>
                <p className="muted" style={{ fontSize: "0.85rem" }}>
                  Formatos permitidos: JPG, PNG, WEBP, PDF (hasta 15 MB)
                </p>
              </div>
            )}
          </div>

          <footer className="modal-acciones">
            <button
              type="button"
              className="boton"
              onClick={cerrar}
              disabled={subiendo}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="boton-primario"
              disabled={!archivo || subiendo}
            >
              {subiendo ? "Subiendo a Azure…" : "Subir archivo"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
